import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, GitCommit } from "lucide-react";

import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const versionApi = axios.create({ baseURL: `${BACKEND}/api/version`, timeout: 8000 });

const ENV_COLORS = {
  production: "bg-emerald-500 text-white border-emerald-600",
  staging:    "bg-amber-500 text-white border-amber-600",
  preview:    "bg-slate-500 text-white border-slate-600",
  development:"bg-slate-400 text-white border-slate-500",
};

/**
 * Compact version pill rendered in the dashboard header.
 * - Green dot if every known phase is deployed
 * - Red dot if any phase is missing (route or DB column not found)
 * - Click → popover with build SHA + full phase checklist
 */
export default function VersionPill() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await versionApi.get("");
        if (!cancelled) { setInfo(data); setError(null); }
      } catch (e) {
        if (!cancelled) setError("unreachable");
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (error) {
    return (
      <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-full border border-rose-300 bg-rose-50 text-rose-700">
        <XCircle className="h-3 w-3" /> backend offline
      </span>
    );
  }
  if (!info) return null;

  const env = (info.build?.environment || "preview").toLowerCase();
  const envClass = ENV_COLORS[env] || ENV_COLORS.preview;
  const allOk = info.phase_count?.missing === 0;
  const sha = info.build?.sha_short || "—";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="version-pill"
          className={[
            "hidden md:inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-full border",
            "hover:opacity-90 transition-opacity",
            envClass,
          ].join(" ")}
          aria-label="Build version"
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${allOk ? "bg-emerald-300" : "bg-rose-400 animate-pulse"}`}
            data-testid="version-pill-dot"
          />
          <span className="uppercase tracking-wider">{env}</span>
          <span className="opacity-80">·</span>
          <GitCommit className="h-3 w-3 opacity-80" />
          <span>{sha}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="version-pill-popover">
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Build</div>
              <div className="font-mono text-sm">{info.build?.sha_short} <span className="text-muted-foreground">· {info.build?.branch}</span></div>
              <div className="text-[10px] text-muted-foreground">{info.build?.built_at || "build time not recorded"}</div>
            </div>
            <span
              className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${envClass}`}
              data-testid="version-pill-env"
            >
              {env}
            </span>
          </div>

          <div className="border-t border-border pt-2.5">
            <div className="text-xs font-medium mb-2 flex items-center justify-between">
              <span>Phases</span>
              <span className="text-[10px] text-muted-foreground font-mono">
                {info.phase_count?.ok}/{info.phase_count?.ok + info.phase_count?.missing}
              </span>
            </div>
            <ul className="space-y-1.5">
              {info.phases?.map((p) => (
                <li key={p.id} className="flex items-start gap-2 text-xs" data-testid={`version-phase-${p.id}`}>
                  {p.ok ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-rose-600 shrink-0 mt-0.5" />
                  )}
                  <span className="min-w-0">
                    <span className="block leading-tight">{p.name}</span>
                    {!p.ok && p.missing?.length > 0 && (
                      <span className="block text-[10px] text-rose-600 font-mono mt-0.5">
                        missing: {p.missing.join(", ")}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {info.phase_count?.missing > 0 && (
            <div className="text-[10px] text-muted-foreground border-t border-border pt-2">
              ⚠️ Re-deploy backend + frontend to push the missing phases live.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
